import { useCallback, useEffect, useRef, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  InfoPage,
  isTouchDevice,
  Link,
  LogSentryException,
  TextField,
} from "@shapr3d/frontend-lib";
import classNames from "classnames";
import { Controller, useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { z } from "zod";

import useNavigation from "../../hooks/navigation";
import { useAuthMethod } from "../../hooks/useAuth";
import { AuthError, AuthMethod, SearchParam } from "../../types";
import { FormWrapper } from "../FormWrapper/FormWrapper";
import Heading from "../Heading/Heading";
import ReCAPTCHA, { type ReCAPTCHAAPI } from "../ReCAPTCHA/ReCAPTCHA";

import style from "./ProjectViewLinkInvitationSignIn.module.css";

const errorMessage = {
  InvalidEmail: "Please enter a valid email address.",
  InvalidPassword: "Please enter your password.",
  InvalidEmailOrPassword: "The password is incorrect.",
  RateLimit: "You tried to log in too many times in a row. Please try again in a few minutes.",
  Unknown: "Something went wrong. Please try again.",
  AuthMethod: "Something went wrong. Please try again.",
  CaptchaError: "Something went wrong. Please refresh the page and try again.",
};

const signInSchema = z.object({
  email: z.string(),
  password: z.string().optional(),
});

type SignInFormData = z.infer<typeof signInSchema>;

interface ProjectViewLinkSignInProps {
  submit: (email: string, password: string, recaptcha?: string) => void;
  inProgress: boolean;
  loginError?: AuthError | null;
  recaptchaLoaded?: boolean;
  emailHint: string;
}

export const ProjectViewLinkInvitationSignIn = ({
  submit,
  inProgress,
  loginError,
  recaptchaLoaded,
  emailHint,
}: ProjectViewLinkSignInProps) => {
  const navigation = useNavigation();
  const [searchParams] = useSearchParams();
  const recaptchaRef = useRef<ReCAPTCHAAPI>(null);
  const passwordFieldRef = useRef<HTMLInputElement>(null);
  const [isCaptchaEnabled, setIsCaptchaEnabled] = useState(false);
  const [isRecaptchaLoaded, setIsRecaptchaLoaded] = useState(recaptchaLoaded);

  const {
    control,
    handleSubmit,
    setError: setFormError,
    clearErrors,
    formState: { errors, isSubmitting },
    getValues,
  } = useForm<SignInFormData>({
    resolver: zodResolver(signInSchema),
    defaultValues: { email: emailHint, password: "" },
  });

  const {
    mutate: mutateAuthMethod,
    data: authMethodData,
    isSuccess: isAuthMethodSuccess,
    isError: isAuthMethodError,
    isPending: isAuthMethodPending,
  } = useAuthMethod();

  const isAuthMethodSSO = authMethodData?.method === AuthMethod.SSO;
  const isAuthMethodPassword = authMethodData?.method === AuthMethod.Password;

  useEffect(() => {
    if (isAuthMethodSuccess && !isTouchDevice() && isAuthMethodPassword) {
      setTimeout(() => passwordFieldRef.current?.focus());
    }
  }, [isAuthMethodSuccess, isAuthMethodPassword]);

  useEffect(() => {
    switch (loginError) {
      case AuthError.InvalidEmailPassword:
        setFormError("root", { message: errorMessage.InvalidEmailOrPassword });
        break;
      case AuthError.RateLimit:
        setFormError("root", { message: errorMessage.RateLimit });
        break;
      case AuthError.MissingCaptcha:
        setIsCaptchaEnabled(true);
        break;
      case AuthError.Unknown:
        setFormError("root", { message: errorMessage.Unknown });
        break;
    }

    if (isAuthMethodError) {
      setFormError("root", { message: errorMessage.AuthMethod });
    }
  }, [loginError, isAuthMethodError, setFormError]);

  useEffect(() => {
    mutateAuthMethod({
      email: emailHint,
      redirectURL: searchParams.get(SearchParam.RedirectUrl),
    });
  }, [emailHint, mutateAuthMethod, searchParams]);

  const processSignIn = useCallback(
    async (data: SignInFormData) => {
      try {
        if (isAuthMethodSSO) {
          window.location.replace(authMethodData.url);
          return;
        }

        if (!data.password || data.password === "") {
          setFormError("password", { message: errorMessage.InvalidPassword });
          return;
        }

        if (!isCaptchaEnabled) {
          submit(data.email, data.password);
          return;
        }

        if (!recaptchaRef.current) {
          setFormError("root", { message: errorMessage.CaptchaError });
          return;
        }

        if (isRecaptchaLoaded) {
          const recaptchaValue = await recaptchaRef.current.execute();

          if (recaptchaValue === "cancel") {
            return;
          }

          if (!recaptchaValue) {
            setFormError("root", { message: errorMessage.CaptchaError });
            return;
          }

          submit(data.email, data.password, recaptchaValue);
        }
      } catch (error) {
        setFormError("root", { message: errorMessage.CaptchaError });
        LogSentryException({ messagePrefix: "Captcha error", error });
      }
    },
    [authMethodData, isAuthMethodSSO, isCaptchaEnabled, isRecaptchaLoaded, setFormError, submit]
  );

  useEffect(() => {
    if (
      isCaptchaEnabled &&
      recaptchaRef.current !== null &&
      loginError === AuthError.MissingCaptcha
    ) {
      processSignIn({
        email: getValues("email"),
        password: getValues("password"),
      });
    }
  }, [isCaptchaEnabled, recaptchaRef, processSignIn, loginError, getValues]);

  const onSubmit = async (data: SignInFormData) => {
    clearErrors();
    await processSignIn(data);
  };

  if (isAuthMethodError) {
    return (
      <InfoPage
        variant="warning"
        title="Something went wrong"
        primaryCTA={<Button onClick={() => navigation.goToSignIn()}>Back to log in</Button>}
      />
    );
  }

  return (
    <FormWrapper className={style.signIn}>
      <div className={style.signInForm}>
        <Heading type="h1">Welcome back</Heading>
        {isAuthMethodPassword && (
          <div className={style.joinViewLinkText}>
            Enter your password for <b className={style.emailHint}>{emailHint}</b> to view the link.
          </div>
        )}

        {isAuthMethodSSO && (
          <div className={style.joinViewLinkText}>
            Log in with <b className={style.emailHint}>{emailHint}</b> to view the link.
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className={style.inputContainer} noValidate>
          {isAuthMethodPassword && (
            <div
              className={classNames(
                style.passwordContainer,
                authMethodData?.method !== AuthMethod.Password && style.hidden
              )}
            >
              <Controller
                name="password"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    ref={passwordFieldRef}
                    errorMessage={errors.password?.message || errors.root?.message}
                    aria-label="password"
                    type="password"
                    placeholder="Password"
                    autoComplete="current-password"
                    error={Boolean(errors.password) || Boolean(errors.root)}
                    disabled={inProgress || isSubmitting}
                    tabIndex={isAuthMethodSuccess ? 0 : -1}
                  />
                )}
              />
              <Link className={style.formLink} onClick={() => navigation.goToPasswordReset()}>
                Forgot password?
              </Link>
            </div>
          )}

          {isCaptchaEnabled && (
            <ReCAPTCHA ref={recaptchaRef} onLoad={() => setIsRecaptchaLoaded(true)} />
          )}

          <Button
            type="submit"
            loading={inProgress || isSubmitting || isAuthMethodPending}
            aria-label={isAuthMethodSSO ? "continue" : "login"}
          >
            <span>{isAuthMethodSSO ? "Continue" : "Log In"}</span>
          </Button>
        </form>
      </div>
    </FormWrapper>
  );
};
